import { relations, sql } from 'drizzle-orm';
import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { user } from './user';
import { adminUser } from './admin';
import { livestream } from './livestream';
import { callRoom } from './calls';
import { breakoutSessionStatus, breakoutSessionMode } from './enums';

// ==========================================
// BREAKOUT SESSION — сессия сессионных залов одной трансляции
// ==========================================
// Одна OPEN-сессия на трансляцию (partial unique). Группирующий слой над
// набором breakout-залов; назначения участников живут независимо от
// фактического подключения к LiveKit (см. breakoutAssignment).
export const breakoutSession = pgTable('breakout_session', {
  id: uuid('id').primaryKey().defaultRandom(),
  // integer FK — Livestream.id is generatedAlwaysAsIdentity integer
  livestreamId: integer('livestream_id').notNull().references(() => livestream.id, { onDelete: 'cascade' }),
  status: breakoutSessionStatus('status').notNull().default('OPEN'),
  mode: breakoutSessionMode('mode').notNull(),
  // integer FK — AdminUser.id
  createdByAdminId: integer('created_by_admin_id').notNull().references(() => adminUser.id),
  openedAt: timestamp('opened_at', { withTimezone: true }).notNull().defaultNow(),
  closedAt: timestamp('closed_at', { withTimezone: true }),
}, (t) => [
  // Гарантия «одна OPEN-сессия на трансляцию»
  uniqueIndex('breakout_session_one_open_per_livestream')
    .on(t.livestreamId)
    .where(sql`status = 'OPEN'`),
  index('breakout_session_livestream_idx').on(t.livestreamId),
]);

// ==========================================
// BREAKOUT ROOM — один сессионный зал (1:1 с LiveKit-комнатой callRoom)
// ==========================================
export const breakoutRoom = pgTable('breakout_room', {
  id: uuid('id').primaryKey().defaultRandom(),
  breakoutSessionId: uuid('breakout_session_id').notNull().references(() => breakoutSession.id, { onDelete: 'cascade' }),
  callRoomId: uuid('call_room_id').notNull().references(() => callRoom.id, { onDelete: 'cascade' }),
  // Стабильный номер зала 1..N (ключ для reassign / адресации сообщений)
  roomIndex: integer('room_index').notNull(),
  label: varchar('label', { length: 64 }).notNull(),
}, (t) => [
  uniqueIndex('breakout_room_session_index_uq').on(t.breakoutSessionId, t.roomIndex),
  uniqueIndex('breakout_room_call_room_uq').on(t.callRoomId),
  index('breakout_room_session_idx').on(t.breakoutSessionId),
]);

// ==========================================
// BREAKOUT ASSIGNMENT — назначение участника в зал
// ==========================================
// Существует до подключения к LiveKit (для авто-попадания и поздних
// зрителей). Активное назначение — releasedAt IS NULL.
export const breakoutAssignment = pgTable('breakout_assignment', {
  id: uuid('id').primaryKey().defaultRandom(),
  breakoutSessionId: uuid('breakout_session_id').notNull().references(() => breakoutSession.id, { onDelete: 'cascade' }),
  breakoutRoomId: uuid('breakout_room_id').notNull().references(() => breakoutRoom.id, { onDelete: 'cascade' }),
  // integer FK — User.id is generatedAlwaysAsIdentity integer
  userId: integer('user_id').notNull().references(() => user.id),
  assignedAt: timestamp('assigned_at', { withTimezone: true }).notNull().defaultNow(),
  releasedAt: timestamp('released_at', { withTimezone: true }),
}, (t) => [
  // Один активный assignment на юзера в сессии (released_at IS NULL)
  uniqueIndex('breakout_assignment_active_uq')
    .on(t.breakoutSessionId, t.userId)
    .where(sql`released_at IS NULL`),
  index('breakout_assignment_room_idx').on(t.breakoutRoomId),
  index('breakout_assignment_user_idx').on(t.userId),
]);

// Зеркалят LivestreamInteractionOption/Telemetry — структура интерактивов
// зала идентична трансляционным (переиспользуем UI и сервис-логику).
export interface BreakoutInteractionOption {
  id: string;
  label: string;
}

export interface BreakoutInteractionTelemetry {
  userAgent: string | null;
  ipHash: string | null;
}

// ==========================================
// BREAKOUT INTERACTION — интерактив (опрос/сбор ответов) в зале(ах)
// ==========================================
// Зеркало LivestreamInteraction, но scoped на breakout-сессию.
// breakout_room_id NULL → интерактив адресован ВСЕМ залам сессии;
// NOT NULL → только конкретному залу. Отдельная таблица (не FK-колонка
// в LivestreamInteraction) — чистая изоляция данных эфира и залов.
export const breakoutInteraction = pgTable('breakout_interaction', {
  id: uuid('id').primaryKey().defaultRandom(),
  breakoutSessionId: uuid('breakout_session_id')
    .notNull()
    .references(() => breakoutSession.id, { onDelete: 'cascade' }),
  // NULL → всем залам сессии; иначе конкретный зал
  breakoutRoomId: uuid('breakout_room_id').references(() => breakoutRoom.id, {
    onDelete: 'cascade',
  }),
  createdByAdminId: integer('created_by_admin_id')
    .notNull()
    .references(() => adminUser.id),
  type: text('type').notNull(), // POLL | QUESTION_COLLECTION
  title: text('title').notNull(),
  description: text('description'),
  status: text('status').notNull().default('ACTIVE'), // ACTIVE | CLOSED
  isAnonymous: boolean('is_anonymous').notNull().default(false),
  allowMultipleOptions: boolean('allow_multiple_options').notNull().default(false),
  allowMultipleSubmissions: boolean('allow_multiple_submissions').notNull().default(false),
  needAnswer: boolean('need_answer').notNull().default(false),
  options: jsonb('options').$type<BreakoutInteractionOption[] | null>(),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updated_at', { withTimezone: true })
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
  closedAt: timestamp('closed_at', { withTimezone: true }),
}, (t) => [
  index('breakout_interaction_session_status_idx').on(t.breakoutSessionId, t.status),
  index('breakout_interaction_room_idx').on(t.breakoutRoomId),
  index('breakout_interaction_admin_idx').on(t.createdByAdminId),
]);

// ==========================================
// BREAKOUT INTERACTION RESPONSE — ответ участника на интерактив зала
// ==========================================
export const breakoutInteractionResponse = pgTable('breakout_interaction_response', {
  id: uuid('id').primaryKey().defaultRandom(),
  interactionId: uuid('interaction_id')
    .notNull()
    .references(() => breakoutInteraction.id, { onDelete: 'cascade' }),
  userId: integer('user_id').references(() => user.id, { onDelete: 'set null' }),
  selectedOptionIds: text('selected_option_ids')
    .array()
    .notNull()
    .default(sql`ARRAY[]::text[]`),
  textValue: text('text_value'),
  isAnswered: boolean('is_answered').notNull().default(false),
  telemetry: jsonb('telemetry').$type<BreakoutInteractionTelemetry | null>(),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updated_at', { withTimezone: true })
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
}, (t) => [
  index('breakout_interaction_response_interaction_idx').on(t.interactionId),
  index('breakout_interaction_response_user_idx').on(t.userId),
]);

// ==========================================
// RELATIONS
// ==========================================

export const breakoutSessionRelations = relations(breakoutSession, ({ one, many }) => ({
  livestream: one(livestream, { fields: [breakoutSession.livestreamId], references: [livestream.id] }),
  createdByAdmin: one(adminUser, { fields: [breakoutSession.createdByAdminId], references: [adminUser.id] }),
  rooms: many(breakoutRoom),
  assignments: many(breakoutAssignment),
}));

export const breakoutRoomRelations = relations(breakoutRoom, ({ one, many }) => ({
  session: one(breakoutSession, { fields: [breakoutRoom.breakoutSessionId], references: [breakoutSession.id] }),
  callRoom: one(callRoom, { fields: [breakoutRoom.callRoomId], references: [callRoom.id] }),
  assignments: many(breakoutAssignment),
}));

export const breakoutAssignmentRelations = relations(breakoutAssignment, ({ one }) => ({
  session: one(breakoutSession, { fields: [breakoutAssignment.breakoutSessionId], references: [breakoutSession.id] }),
  room: one(breakoutRoom, { fields: [breakoutAssignment.breakoutRoomId], references: [breakoutRoom.id] }),
  user: one(user, { fields: [breakoutAssignment.userId], references: [user.id] }),
}));

export const breakoutInteractionRelations = relations(breakoutInteraction, ({ one, many }) => ({
  session: one(breakoutSession, {
    fields: [breakoutInteraction.breakoutSessionId],
    references: [breakoutSession.id],
  }),
  room: one(breakoutRoom, {
    fields: [breakoutInteraction.breakoutRoomId],
    references: [breakoutRoom.id],
  }),
  createdByAdmin: one(adminUser, {
    fields: [breakoutInteraction.createdByAdminId],
    references: [adminUser.id],
  }),
  responses: many(breakoutInteractionResponse),
}));

export const breakoutInteractionResponseRelations = relations(
  breakoutInteractionResponse,
  ({ one }) => ({
    interaction: one(breakoutInteraction, {
      fields: [breakoutInteractionResponse.interactionId],
      references: [breakoutInteraction.id],
    }),
    user: one(user, {
      fields: [breakoutInteractionResponse.userId],
      references: [user.id],
    }),
  }),
);
